import React, { useCallback, useEffect, useMemo, useState } from 'react';
import VisibilityApi from '_legacy/api/visibilityApi';
import { userSelectors } from 'store/feature/userSlice';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Switch,
    TextField,
    Typography,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Divider,
    Autocomplete,
    CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import GroupIcon from '@mui/icons-material/Group';
import _ from 'lodash';
import { useAppSelector } from 'store/hooks';
import { StyledInfoRow } from './StyledInfoRow';
import { formatDefaultDateTime } from '_legacy/components/common/DateTimeFormatUtils';
import WarningIconOrange from '_legacy/components/icons/WarningIconOrange';

interface User {
    id: string;
    name: string;
    isYou?: boolean;
    isOwner?: boolean;
}

interface Group {
    id: string;
    name: string;
}

type Sharer = User | Group;

interface Visibility {
    userIds: number[];
    groupIds: string[];
}

interface EntityDetails {
    id: string;
    laycan: {
        shorthand: string;
    };
    cargoParts: {
        name: string;
    }[];
    loadLocationParts: {
        name: string;
    }[];
    dischargeLocationParts: {
        name: string;
    }[];
    charterer: { name: string };
    thirdPartyCharterer: {
        name: string;
    };
    groupId: string;
    groupName: string;
    createdBy: {
        fullName: string;
        userId: number;
        username: string;
    };
    visibility: Visibility;
    __typename: string;
    groupOnlyExpiryDate?: any;
}

interface VisibilityModalProps {
    open: boolean;
    onClose: (isSaved: boolean, isCanceled: boolean) => void;
    onSave: (
        result: any,
        sharedWith: Sharer[],
        previousSharers: Sharer[]
    ) => void;
    entityDetails: EntityDetails;
    gridId: 'orders' | 'fixtures';
}

const isUser = (item: Sharer): item is User => {
    return !isNaN(item.id as any);
};

const VisibilityModal: React.FC<VisibilityModalProps> = ({
    open,
    onClose,
    onSave,
    entityDetails,
    gridId,
}) => {
    const userId = useAppSelector(userSelectors.userId);
    const datasetId = useAppSelector(userSelectors.currentDatasetId);
    const userPermissions = useAppSelector(userSelectors.userPermissions);
    const userGroup = useAppSelector(userSelectors.userSelectedGroup);
    const owner: Sharer = {
        id: entityDetails.createdBy.userId.toString(),
        name:
            entityDetails.createdBy.fullName ??
            entityDetails.createdBy.username,
        isOwner: true,
        isYou: entityDetails.createdBy.userId === userId,
    };
    const defaultIsPublic =
        !entityDetails.visibility?.groupIds?.length &&
        !entityDetails.visibility?.userIds?.length;

    const [isPublic, setIsPublic] = useState<boolean>(defaultIsPublic);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [sharedWith, setSharedWith] = useState<Sharer[]>([owner]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [options, setOptions] = useState<Sharer[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [selectedOption, setSelectedOption] = useState<Sharer | null>(null);
    const [defaultSharers, setDefaultSharers] = useState<Sharer[]>([owner]);

    const canEdit = useMemo(() => {
        const groupPermission = userPermissions
            .filter((p) => p.canWrite)
            .find((p) => p.groupId === entityDetails.groupId);
        return !!groupPermission;
    }, [entityDetails.groupId, userPermissions]);

    const debouncedSearch = useCallback(
        _.debounce(async (query: string) => {
            if (query.trim().length < 2) {
                setOptions([]);
                setLoading(false);
                return;
            }

            try {
                const results = await VisibilityApi.search(query, datasetId);
                const newResults = filterOutExistingSharers(results);
                setOptions(newResults);
            } catch (error) {
                console.error('Error searching:', error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        }, 300),
        []
    );

    useEffect(() => {
        if (searchQuery.trim().length >= 2) {
            setLoading(true);
            debouncedSearch(searchQuery);
        } else {
            setOptions([]);
            setLoading(false);
        }
        return () => {
            debouncedSearch.cancel();
        };
    }, [searchQuery, debouncedSearch]);

    useEffect(() => {
        if (
            !_.isEqual(sharedWith, defaultSharers) ||
            isPublic !== defaultIsPublic
        ) {
            setIsDirty(true);
        } else {
            setIsDirty(false);
        }
    }, [sharedWith, isPublic, defaultSharers, defaultIsPublic]);

    useEffect(() => {
        async function getSharers() {
            var sharers = [];
            if (
                entityDetails.visibility?.userIds ||
                entityDetails.visibility?.groupIds
            ) {
                sharers = await VisibilityApi.get(
                    entityDetails.visibility?.userIds,
                    entityDetails.visibility?.groupIds
                );
            }
            const existingSharers = filterOutExistingSharers(sharers);
            const allSharers = sharedWith.concat(existingSharers);
            setSharedWith(allSharers);
            setDefaultSharers(allSharers);
        }
        getSharers();
    }, [entityDetails.visibility?.userIds, entityDetails.visibility?.groupIds]);

    const handleTogglePublic = () => {
        setIsPublic(!isPublic);
    };

    const filterOutExistingSharers = (newSharers: Sharer[]) => {
        return newSharers.filter(
            (x) => !sharedWith.map((y) => y.id).includes(x.id)
        );
    };

    const handleChange = (option: Sharer | null) => {
        if (option) {
            if (isUser(option) && option.id === userId.toString()) {
                option.isYou = true;
            }
            setSharedWith([...sharedWith, option]);
            setSelectedOption(null);
            setSearchQuery('');
        }
    };

    const handleRemove = (id: string) => {
        if (id !== entityDetails.createdBy.userId.toString()) {
            setSharedWith(sharedWith.filter((item) => item.id !== id));
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        const isFixturesGrid = gridId === 'fixtures';
        try {
            if (isPublic) {
                const result = await VisibilityApi.update(
                    entityDetails.id,
                    [],
                    [],
                    isFixturesGrid
                );
                onSave(result, [], defaultSharers);
            } else {
                const userIds = sharedWith
                    .filter((x) => isUser(x))
                    .map((x) => Number.parseInt(x.id));
                const groupIds = sharedWith
                    .filter((x) => !isUser(x))
                    .map((x) => x.id);
                const result = await VisibilityApi.update(
                    entityDetails.id,
                    userIds,
                    groupIds,
                    isFixturesGrid
                );
                onSave(result, sharedWith, defaultSharers);
            }
        } catch (error) {
            console.error('Error saving:', error);
        } finally {
            setIsSaving(false);
            setIsDirty(false);
            onClose(true, false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => onClose(false, false)}
            maxWidth="sm"
            fullWidth
            slotProps={{ paper: { sx: { borderRadius: 1 } } }}
        >
            <DialogTitle variant="h4">Change visibility</DialogTitle>
            <Divider />
            <DialogContent>
                <Grid container columnSpacing={4}>
                    <StyledInfoRow
                        label="L/C"
                        value={entityDetails.laycan?.shorthand}
                    />
                    <StyledInfoRow
                        namedItems={entityDetails.cargoParts}
                        label="Cargo"
                    />
                    <StyledInfoRow
                        namedItems={entityDetails.loadLocationParts}
                        label="Load"
                    />
                    <StyledInfoRow
                        namedItems={entityDetails.dischargeLocationParts}
                        label="Discharge"
                    />
                    <StyledInfoRow
                        label="Charterer"
                        value={
                            entityDetails.thirdPartyCharterer?.name ??
                            entityDetails.charterer?.name
                        }
                    />
                    <StyledInfoRow
                        label="Group"
                        value={entityDetails.groupName}
                    />
                </Grid>
            </DialogContent>
            <Divider />
            <DialogContent>
                <CanEditWarrning canEdit={canEdit} />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Switch
                            checked={isPublic}
                            onChange={handleTogglePublic}
                            color="primary"
                            disabled={!canEdit}
                        />
                        <Typography>{`Anyone in ${userGroup?.name} can view`}</Typography>
                    </Box>

                    {entityDetails.groupOnlyExpiryDate && (
                        <>
                            <Divider
                                sx={{ ml: 1, mr: 1 }}
                                orientation="vertical"
                                flexItem
                            />
                            <Typography>
                                {`${
                                    entityDetails.__typename
                                } will become visible to all groups on
                            ${formatDefaultDateTime(
                                entityDetails.groupOnlyExpiryDate
                            )}`}
                            </Typography>
                        </>
                    )}
                </Box>

                {!isPublic && canEdit && (
                    <Box display="flex">
                        <Autocomplete
                            fullWidth
                            filterOptions={(x) => x}
                            options={options}
                            loading={loading}
                            value={selectedOption}
                            inputValue={searchQuery}
                            size="small"
                            noOptionsText="Enter at least 2 characters"
                            onChange={(_event, newValue) =>
                                handleChange(newValue)
                            }
                            onInputChange={(_event, newInputValue) =>
                                setSearchQuery(newInputValue)
                            }
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search individuals or groups"
                                    variant="outlined"
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loading ? (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={20}
                                                        />
                                                    ) : null}
                                                </>
                                            ),
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                )}
                <List sx={{ width: '100%' }}>
                    {sharedWith.map((item) => (
                        <React.Fragment key={item.id}>
                            <ListItem
                                secondaryAction={
                                    !(isUser(item) && item.isOwner) ? (
                                        <Button
                                            color="primary"
                                            onClick={() =>
                                                handleRemove(item.id)
                                            }
                                            disabled={!canEdit}
                                        >
                                            REMOVE
                                        </Button>
                                    ) : (
                                        <Typography
                                            sx={{ marginRight: 1 }}
                                            variant="body1"
                                            color="text.secondary"
                                        >
                                            Owner
                                        </Typography>
                                    )
                                }
                            >
                                <ListItemAvatar>
                                    {isUser(item) ? (
                                        <Avatar
                                            sx={{
                                                bgcolor: 'grey.400',
                                            }}
                                        >
                                            {item.name
                                                ?.substring(0, 2)
                                                .toUpperCase()}
                                        </Avatar>
                                    ) : (
                                        <Avatar
                                            sx={{
                                                bgcolor: 'grey.400',
                                            }}
                                        >
                                            <GroupIcon />
                                        </Avatar>
                                    )}
                                </ListItemAvatar>
                                <ListItemText
                                    sx={{ maxWidth: '400px' }}
                                    primary={
                                        <Typography>
                                            {item.name}
                                            {isUser(item) &&
                                                item.isYou &&
                                                ' (You)'}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                            <Divider component="li" />
                        </React.Fragment>
                    ))}
                </List>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => onClose(false, true)} variant="outlined">
                    CANCEL
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isDirty || !canEdit || isSaving}
                >
                    SAVE CHANGES
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const CanEditWarrning: React.FC<{ canEdit: boolean }> = (props: {
    canEdit: boolean;
}) => {
    if (props.canEdit) {
        return null;
    }

    return (
        <span
            style={{
                fontSize: 'x-small',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 10,
            }}
        >
            <WarningIconOrange width={15} height={15} />
            <tr
                style={{
                    fontWeight: 600,
                }}
            >
                {'Note:'}&nbsp;
            </tr>
            You are not permitted to edit the visibility settings for this item.
            If you require them, please contact Support.
        </span>
    );
};

export default VisibilityModal;
